/**
 * TeamCity API Service
 * Communicates with the backend server which handles TeamCity REST API calls
 */

class TeamCityAPI {
  constructor(getToken = null) {
    // In production, use relative URL (same server serves API and frontend)
    // In development, use localhost:3151
    if (process.env.NODE_ENV === 'production') {
      this.apiUrl = '/api';
    } else if (process.env.REACT_APP_API_URL) {
      this.apiUrl = process.env.REACT_APP_API_URL;
    } else {
      const hostname = window.location.hostname;
      this.apiUrl = `http://${hostname}:3151/api`;
    }
    this.getToken = getToken;
  }

  /**
   * Set the token getter function
   */
  setTokenGetter(getToken) {
    this.getToken = getToken;
  }

  /**
   * Make a request to the backend API
   */
  async request(endpoint, options = {}) {
    const url = `${this.apiUrl}${endpoint}`;

    const headers = {
      'Content-Type': 'application/json',
    };

    // Add auth token if available
    if (this.getToken) {
      const token = await this.getToken();
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
    }

    try {
      const response = await fetch(url, {
        headers,
        ...options,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || `API error: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('API request failed:', error);
      throw error;
    }
  }

  /**
   * Get multiple builds information
   */
  async getBuildsInfo(buildTypeIds) {
    try {
      return await this.request('/builds/info', {
        method: 'POST',
        body: JSON.stringify({ buildTypeIds }),
      });
    } catch (error) {
      console.error('Failed to get builds info:', error);
      throw error;
    }
  }

  /**
   * Test connection to TeamCity server
   */
  async testConnection() {
    try {
      return await this.request('/test-connection');
    } catch (error) {
      return { success: false, message: error.message };
    }
  }

  /**
   * Check if the backend API is healthy (no auth required)
   */
  async checkHealth() {
    const url = `${this.apiUrl}/health`;
    try {
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      return { status: 'error', message: error.message };
    }
  }

  /**
   * Get all build configurations
   */
  async getBuildConfigs() {
    try {
      return await this.request('/build-configs');
    } catch (error) {
      console.error('Failed to get build configs:', error);
      throw error;
    }
  }

  /**
   * Get currently running builds
   */
  async getRunningBuilds() {
    try {
      return await this.request('/running-builds');
    } catch (error) {
      console.error('Failed to get running builds:', error);
      throw error;
    }
  }

  /**
   * Get queued builds
   */
  async getQueuedBuilds() {
    try {
      return await this.request('/queued-builds');
    } catch (error) {
      console.error('Failed to get queued builds:', error);
      throw error;
    }
  }

  /**
   * Get agent health info (disk space)
   */
  async getAgentHealth() {
    try {
      return await this.request('/agents/health');
    } catch (error) {
      console.error('Failed to get agent health:', error);
      throw error;
    }
  }

  /**
   * Get all agents and their current status
   */
  async getAgents() {
    try {
      return await this.request('/agents');
    } catch (error) {
      console.error('Failed to get agents:', error);
      throw error;
    }
  }

  /**
   * Get all available build types from TeamCity
   */
  async getAllBuildTypes() {
    try {
      return await this.request('/build-types');
    } catch (error) {
      console.error('Failed to get build types:', error);
      throw error;
    }
  }

  /**
   * Get all projects from TeamCity
   */
  async getProjects() {
    try {
      return await this.request('/projects');
    } catch (error) {
      console.error('Failed to get projects:', error);
      throw error;
    }
  }

  /**
   * Get user preferences
   */
  async getUserPreferences() {
    try {
      return await this.request('/user/preferences');
    } catch (error) {
      console.error('Failed to get user preferences:', error);
      throw error;
    }
  }

  /**
   * Save user preferences
   */
  async saveUserPreferences(buildConfigs, showPersonalBuilds = false, enableDebugging = false, showQueuedBuilds = true) {
    try {
      return await this.request('/user/preferences', {
        method: 'POST',
        body: JSON.stringify({ buildConfigs, showPersonalBuilds, enableDebugging, showQueuedBuilds }),
      });
    } catch (error) {
      console.error('Failed to save user preferences:', error);
      throw error;
    }
  }

  // ── Build runner: read endpoints ──────────────────────────────────────────

  /**
   * Get the curated list of builds runnable from the build runner.
   */
  async getAllowedBuilds() {
    return this.request('/allowed-builds');
  }

  /**
   * Get deployed Orka VMs correlated with their TeamCity agent (build machines).
   */
  async getBuildMachines() {
    return this.request('/orka/build-machines');
  }

  /**
   * Get the current user's Pokédex-triggered builds -> {queued, running, finished}.
   */
  async getMyBuilds() {
    return this.request('/builds/mine');
  }

  /**
   * Cancel a queued build or stop a running one. options: { queued }.
   */
  async cancelBuild(buildId, { queued } = {}) {
    return this.request(`/builds/${buildId}/cancel`, {
      method: 'POST',
      body: JSON.stringify({ queued }),
    });
  }

  /**
   * Get configurable parameters for a build type.
   */
  async getBuildParameters(buildTypeId) {
    return this.request(`/build-types/${buildTypeId}/parameters`);
  }

  /**
   * Get VCS roots (id, name, Perforce stream) for a build type.
   */
  async getVcsRoots(buildTypeId) {
    return this.request(`/build-types/${buildTypeId}/vcs-roots`);
  }

  // ── Build runner: Perforce changelists (Swarm) ────────────────────────────

  /**
   * Get the logged-in user's shelved changelists, optionally filtered to a branch
   * (a VCS-root stream/depot path).
   */
  async getShelvedChangelists(path) {
    const q = path ? `?path=${encodeURIComponent(path)}` : '';
    return this.request(`/changelists/shelved${q}`);
  }

  /**
   * Get recent submitted changelists for the "Include changes" picker, optionally
   * filtered to a branch.
   */
  async getRecentChangelists(count = 20, path) {
    const q = path ? `&path=${encodeURIComponent(path)}` : '';
    return this.request(`/changelists/recent?count=${count}${q}`);
  }

  /**
   * Get a single changelist's description + files + stream.
   */
  async getChangelist(changelistId) {
    return this.request(`/changelists/${changelistId}`);
  }

  // ── Build runner: trigger + last-used ─────────────────────────────────────

  /**
   * Trigger a (optionally personal) build. options: { changelist, agentId,
   * personal, baseCL, customParameters }.
   */
  async triggerBuild(buildTypeId, options = {}) {
    return this.request('/builds/trigger', {
      method: 'POST',
      body: JSON.stringify({ buildTypeId, ...options }),
    });
  }

  /**
   * Get the user's last-used run settings for a build type ({} if none).
   */
  async getLastUsed(buildTypeId) {
    return this.request(`/run-build/last-used/${buildTypeId}`);
  }

  /**
   * Persist the user's last-used run settings for a build type.
   */
  async saveLastUsed(buildTypeId, settings) {
    return this.request(`/run-build/last-used/${buildTypeId}`, {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  }

  // ── Admin management ──────────────────────────────────────────────────────

  async getAdminUsers() {
    return this.request('/admin/users');
  }

  async addAdminUser(email) {
    return this.request('/admin/users', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async removeAdminUser(email) {
    return this.request(`/admin/users/${encodeURIComponent(email)}`, {
      method: 'DELETE',
    });
  }
}

export default TeamCityAPI;
